import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  languageOptionsAtom,
  selectedValuesAtom,
  voicesDescriptionAtom,
  selectedVoiceLangAtom,
} from "atoms";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  Grid,
  Stack,
  Typography,
  Dialog,
  useMediaQuery,
  useTheme,
  Slide,
  Button,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import {
  generateLanguageOptions,
  getCategorizedVoices,
  getLanguageLabel,
} from "shared/utils/tts";
import InlineAlert from "shared/components/InlineAlert";
import { usePlayVoice } from "shared/hooks/usePlayVoice";
import VoiceItem from "features/TTS/components/VoiceItem";
import CustomAccordion from "./CustomAccordion";
import { CustomDialog } from "@web-applications/allinga-web-components";
import LanguageItem from "features/TTS/components/LanguageItem";
import React from "react";
import { MenuBorderColor } from "shared/constants";

export interface LanguageVoiceCatalogProps {
  handleCloseDialog: () => void;
}

const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function LanguageVoiceCatalog({
  handleCloseDialog,
}: LanguageVoiceCatalogProps): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // 600px and below

  const languageOptions = useAtomValue(languageOptionsAtom);
  const voicesDescription = useAtomValue(voicesDescriptionAtom);
  const [selectedValues, setSelectedValues] = useAtom(selectedValuesAtom);
  const selectedVoiceLang = useSetAtom(selectedVoiceLangAtom);
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement>();

  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    selectedValues.selectedLanguage
  );
  const [selectedVoice, setSelectedVoice] = useState<string>(
    selectedValues.selectedVoice
  );
  const [nativeVoices, setNativeVoices] = useState<string[]>([]);
  const [nonNativeVoices, setNonNativeVoices] = useState<string[]>([]);
  const { handlePlayClick } = usePlayVoice();

  const [nativeAccordionExpanded, setNativeAccordionExpanded] =
    useState(!isMobile);
  const [moreVoicesAccordionExpanded, setMoreVoicesAccordionExpanded] =
    useState(false);
  const [moreLanguagesAccordionExpanded, setMoreLanguagesAccordionExpanded] =
    useState(false);

  const [currentPlayingVoiceName, setCurrentPlayingVoiceName] =
    useState<string>("");
  const [progressVoiceName, setProgressVoiceName] = useState<string>("");
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [isLanguageChange, setIsLanguageChange] = useState(false);

  const selectedVoiceRef = useRef<HTMLDivElement>(null);
  const nativeAccordionRef = useRef<HTMLDivElement>(null);
  const moreVoicesAccordionRef = useRef<HTMLDivElement>(null);
  const moreLanguagesAccordionRef = useRef<HTMLDivElement>(null);

  const [accordionScrollTarget, setAccordionScrollTarget] = useState<
    string | null
  >(null);

  const translatedOptions = generateLanguageOptions(t, languageOptions);

  useEffect(() => {
    const { native, nonNative } = getCategorizedVoices(
      selectedLanguage,
      voicesDescription
    );
    setNativeVoices(native);
    setNonNativeVoices(nonNative);

    // Auto-select voice based on language
    const languageCode = selectedLanguage.toLowerCase();

    if (languageCode === "de-de" || languageCode === "de") {
      // For German, keep Annie as default if it exists in native voices
      const annieExists = native.find(
        (voice) => voice.toLowerCase() === "annie"
      );
      if (annieExists) {
        setSelectedVoice(annieExists);
      } else if (native.length > 0) {
        setSelectedVoice(native[0]);
      } else if (nonNative.length > 0) {
        setSelectedVoice(nonNative[0]);
      }
    } else {
      // For other languages, select first native voice
      if (native.length > 0) {
        setSelectedVoice(native[0]);
      } else if (nonNative.length > 0) {
        setSelectedVoice(nonNative[0]);
      }
    }
  }, [selectedLanguage, voicesDescription]);

  useEffect(() => {
    selectedVoiceLang(selectedLanguage);
  }, [selectedLanguage, selectedVoiceLang]);

  // Handle scrolling to selected voice and expanding accordions
  useEffect(() => {
    if (!selectedVoice) return;

    const isInNonNative = nonNativeVoices.includes(selectedVoice);

    if (isInNonNative) {
      setMoreVoicesAccordionExpanded(true);
    }

    if ((isInitialMount || !isLanguageChange) && selectedVoiceRef.current) {
      const timeoutId = setTimeout(() => {
        selectedVoiceRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        if (isInitialMount) {
          setIsInitialMount(false);
        }
        if (isLanguageChange) {
          setIsLanguageChange(false);
        }
      }, 400);

      return () => clearTimeout(timeoutId);
    } else if (isLanguageChange) {
      setIsLanguageChange(false);
    }
  }, [selectedVoice, nonNativeVoices, isInitialMount, isLanguageChange]);

  useEffect(() => {
    if (!accordionScrollTarget) return;

    const refMap: Record<string, React.RefObject<HTMLDivElement>> = {
      "native-voices": nativeAccordionRef,
      "more-voices": moreVoicesAccordionRef,
      "more-languages": moreLanguagesAccordionRef,
    };

    const targetRef = refMap[accordionScrollTarget];

    const timeoutId = setTimeout(() => {
      targetRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setAccordionScrollTarget(null);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [accordionScrollTarget]);

  const handleLanguageChange = (newLanguage: string) => {
    setIsLanguageChange(true);
    setSelectedLanguage(newLanguage);
    selectedVoiceLang(newLanguage);
  };

  const handleVoiceSelection = (voice: string) => {
    setSelectedVoice(voice);
    setIsLanguageChange(false);
  };

  const handleClose = () => {
    audioPlayer?.pause();
    setTimeout(() => {
      handleCloseDialog();
    }, 500);
  };

  const handleApplyClick = () => {
    setSelectedValues({
      ...selectedValues,
      selectedLanguage,
      selectedVoice,
    });
    selectedVoiceLang(selectedLanguage);
    handleClose();
  };

  const filteredNativeVoices = nativeVoices.filter((v) => v !== selectedVoice);
  const filteredNonNativeVoices = nonNativeVoices.filter(
    (v) => v !== selectedVoice
  );

  const renderVoiceItem = (
    voice: string,
    opts?: { ref?: React.Ref<HTMLDivElement> }
  ) => (
    <Grid item xs={12} md={6} lg={4} key={voice} ref={opts?.ref}>
      <VoiceItem
        voice={voice}
        handleVoiceSelection={() => handleVoiceSelection(voice)}
        isSelected={selectedVoice === voice}
        handlePlayClick={() =>
          handlePlayClick(
            voice,
            selectedLanguage,
            audioPlayer,
            setAudioPlayer,
            setCurrentPlayingVoiceName,
            setProgressVoiceName,
            currentPlayingVoiceName
          )
        }
        showStopButton={currentPlayingVoiceName === voice}
        showInProgress={progressVoiceName === voice}
      />
    </Grid>
  );

  const getVoiceItems = (voices: string[]) =>
    voices.map((voice) => renderVoiceItem(voice));

  const selectedVoiceSection = selectedVoice ? (
    <Stack sx={{ mb: 2 }}>
      <Grid container spacing={2}>
        {renderVoiceItem(selectedVoice)}
      </Grid>
    </Stack>
  ) : null;

  const getLanguageItems = () => {
    return translatedOptions.map((language) => (
      <Grid item xs={12} md={6} lg={4} key={language.value}>
        <LanguageItem
          languageInfo={language}
          handleLanguageSelection={() => handleLanguageChange(language.value)}
          isSelected={selectedLanguage === language.value}
        />
      </Grid>
    ));
  };

  const dialogContent = (
    <Stack sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Step 1: Script language */}
      <Stack sx={{ py: "20px" }} gap={2}>
        <Typography variant="subtitle1">
          {t("tts.catalog.languageTitle")}
        </Typography>
        <Grid container spacing={2}>
          {languageOptions.length <= 1 ? (
            <InlineAlert
              message={t("tts.catalog.inlineAlertMsg", {
                what: languageOptions?.[0],
              })}
            />
          ) : (
            getLanguageItems()
          )}
        </Grid>
      </Stack>

      {/* Step 2: Script voice */}
      <Stack
        sx={{ py: "20px", flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Typography variant="subtitle1">
          {t("tts.catalog.voiceTitle")}
        </Typography>
        {/* Native and non-native speakers section */}
        <Stack sx={{ flex: 1 }}>
          <div ref={nativeAccordionRef}>
            <CustomAccordion
              id="native-voices"
              expanded={nativeAccordionExpanded}
              onChange={(_, isExpanded) => {
                setNativeAccordionExpanded(isExpanded);
                if (isExpanded) setAccordionScrollTarget("native-voices");
              }}
              headerText={t("tts.catalog.nativeVoices", {
                what: getLanguageLabel(selectedLanguage, languageOptions),
              })}
              headerVariant="body1"
            >
              {nativeVoices.length > 0 ? (
                <Grid container spacing={2}>
                  {getVoiceItems(nativeVoices)}
                </Grid>
              ) : (
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  pt={2}
                  pb={2}
                >
                  <Typography variant="body1" color="text.secondary">
                    {t("tts.catalog.noSpeakerFound", {
                      what: getLanguageLabel(selectedLanguage, languageOptions),
                    })}
                  </Typography>
                </Stack>
              )}
            </CustomAccordion>
          </div>
          <div ref={moreVoicesAccordionRef}>
            <CustomAccordion
              id="more-voices"
              expanded={moreVoicesAccordionExpanded}
              onChange={(_, isExpanded) => {
                setMoreVoicesAccordionExpanded(isExpanded);
                if (isExpanded) setAccordionScrollTarget("more-voices");
              }}
              headerText={t("tts.catalog.moreVoices")}
              headerVariant="body1"
            >
              <Grid container spacing={2}>
                {getVoiceItems(nonNativeVoices)}
              </Grid>
            </CustomAccordion>
          </div>
        </Stack>
      </Stack>
    </Stack>
  );

  // Mobile popup version
  if (isMobile) {
    const displayedLanguages = translatedOptions;

    return (
      <Dialog
        open
        fullScreen
        onClose={handleClose}
        TransitionComponent={SlideTransition}
        keepMounted
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <Stack
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              p: 2,
              borderBottom: `1px solid ${MenuBorderColor}`,
              flexShrink: 0,
            }}
          >
            <Typography variant="h6">{t("tts.catalog.header")}</Typography>
            <button
              onClick={handleClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "28px",
                cursor: "pointer",
                padding: "4px",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </Stack>

          {/* Content - Scrollable */}
          <Stack
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
            }}
          >
            {/* Language Section */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              {t("tts.catalog.languageTitle")}
            </Typography>
            <Stack
              spacing={0}
              sx={{
                mb: 3,
                maxHeight: 300,
                overflowY: "auto",
                pr: 1,
                minHeight: "180px",
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#ccc",
                  borderRadius: "3px",
                },
              }}
            >
              {displayedLanguages
                .filter(
                  (lang) => lang.value === "de-DE" || lang.value === "en-GB"
                )
                .map((language) => (
                  <LanguageItem
                    key={language.value}
                    languageInfo={language}
                    handleLanguageSelection={() =>
                      handleLanguageChange(language.value)
                    }
                    isSelected={selectedLanguage === language.value}
                  />
                ))}

              {/* More Languages Accordion - All other languages */}
              <div ref={moreLanguagesAccordionRef}>
                <CustomAccordion
                  id="more-languages"
                  expanded={moreLanguagesAccordionExpanded}
                  onChange={(_, isExpanded) => {
                    setMoreLanguagesAccordionExpanded(isExpanded);
                    if (isExpanded) setAccordionScrollTarget("more-languages");
                  }}
                  headerText={t("tts.catalog.moreLanguages")}
                  headerVariant="body1"
                >
                  <Stack spacing={0}>
                    {displayedLanguages
                      .filter(
                        (lang) =>
                          lang.value !== "de-DE" && lang.value !== "en-GB"
                      )
                      .map((language) => (
                        <LanguageItem
                          key={language.value}
                          languageInfo={language}
                          handleLanguageSelection={() =>
                            handleLanguageChange(language.value)
                          }
                          isSelected={selectedLanguage === language.value}
                        />
                      ))}
                  </Stack>
                </CustomAccordion>
              </div>
            </Stack>

            {/* Voice Section */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              {t("tts.catalog.voiceTitle")}
            </Typography>
            {/* Currently selected voice*/}
            {selectedVoiceSection}

            <Stack>
              <div ref={nativeAccordionRef}>
                <CustomAccordion
                  id="native-voices"
                  expanded={nativeAccordionExpanded}
                  onChange={(_, isExpanded) => {
                    setNativeAccordionExpanded(isExpanded);
                    if (isExpanded) setAccordionScrollTarget("native-voices");
                  }}
                  headerText={t("tts.catalog.nativeVoices", {
                    what: getLanguageLabel(selectedLanguage, languageOptions),
                  })}
                  headerVariant="body1"
                >
                  {filteredNativeVoices.length > 0 ? (
                    <Grid container spacing={2}>
                      {getVoiceItems(filteredNativeVoices)}
                    </Grid>
                  ) : (
                    <Stack
                      justifyContent="center"
                      alignItems="center"
                      pt={2}
                      pb={2}
                    >
                      <Typography variant="body1" color="text.secondary">
                        {t("tts.catalog.noSpeakerFound", {
                          what: getLanguageLabel(
                            selectedLanguage,
                            languageOptions
                          ),
                        })}
                      </Typography>
                    </Stack>
                  )}
                </CustomAccordion>
              </div>
              <div ref={moreVoicesAccordionRef}>
                <CustomAccordion
                  id="more-voices"
                  expanded={moreVoicesAccordionExpanded}
                  onChange={(_, isExpanded) => {
                    setMoreVoicesAccordionExpanded(isExpanded);
                    if (isExpanded) setAccordionScrollTarget("more-voices");
                  }}
                  headerText={t("tts.catalog.moreVoices")}
                  headerVariant="body1"
                >
                  <Grid container spacing={2}>
                    {getVoiceItems(filteredNonNativeVoices)}
                  </Grid>
                </CustomAccordion>
              </div>
            </Stack>
          </Stack>

          {/* Action buttons */}
          <Stack
            direction="row"
            gap={2}
            sx={{
              p: 2,
              borderTop: `1px solid ${MenuBorderColor}`,
              flexShrink: 0,
              justifyContent: "flex-end",
            }}
          >
            <Button variant="text" onClick={handleClose} color="inherit">
              {t("buttons.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyClick}
              color="primary"
            >
              {t("buttons.apply")}
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    );
  }

  // Desktop version (CustomDialog)
  return (
    <CustomDialog
      header={t("tts.catalog.header")}
      showDivider
      handleOnClose={handleClose}
      onPrimaryButtonClick={handleApplyClick}
      primaryButtonText={t("buttons.apply")}
      tertiaryButtonText={t("buttons.cancel")}
      onTertiaryButtonClick={handleClose}
      maxWidth="md"
      height="auto"
      maxHeight={window.innerHeight - 32}
    >
      {dialogContent}
    </CustomDialog>
  );
}
